import React, { useState, useEffect } from "react";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	List,
	ListItemButton,
	ListItemText,
	Typography,
	Box,
	CircularProgress,
} from "@mui/material";
import { useUser } from "../../context/UserContext";

function UnidadeSelectorModal({ open, onSelect }) {
	const { user, loading: userLoading } = useUser();
	const [unidades, setUnidades] = useState([]);
	const [selectedUnidade, setSelectedUnidade] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState("");

	useEffect(() => {
		if (!open) return;

		// Aguarda o perfil terminar de carregar no UserContext.
		if (userLoading) {
			setLoading(true);
			return;
		}

		setLoading(false);
		setError("");

		// Extrair as unidades do perfil mantido em memória pelo UserContext.
		const unidadesArray = (user?.unidadesDistribuidoras || []).map(
			(rel) => rel.unidadeDistribuidora
		);

		if (!user || unidadesArray.length === 0) {
			// Sem unidades vinculadas: enviar para o cadastro.
			window.location.href = "/distribuidora/cadastro";
			return;
		}

		setUnidades(unidadesArray);
	}, [open, user, userLoading]);

	const handleSelectUnidade = (unidade) => {
		setSelectedUnidade(unidade);
	};

	const handleConfirm = () => {
		if (selectedUnidade) {
			localStorage.setItem("selected_unidade_id", selectedUnidade.id.toString());
			if (selectedUnidade.empresaDistribuidorId) {
				localStorage.setItem("selected_empresa_id", selectedUnidade.empresaDistribuidorId.toString());
			}
			onSelect(selectedUnidade);
		}
	};

	return (
		<Dialog
			open={open}
			maxWidth="sm"
			fullWidth
			disableEscapeKeyDown
			sx={{
				"& .MuiDialog-paper": {
					borderRadius: "16px",
					padding: "16px",
				},
			}}
		>
			<DialogTitle sx={{ textAlign: "center", fontWeight: 600, fontSize: "24px", color: "#1a9e8f" }}>
				Selecione uma Unidade Distribuidora
			</DialogTitle>

			<DialogContent>
				{loading ? (
					<Box sx={{ display: "flex", justifyContent: "center", padding: 4 }}>
						<CircularProgress />
					</Box>
				) : error ? (
					<Typography color="error" sx={{ textAlign: "center", padding: 2 }}>
						{error}
					</Typography>
				) : unidades.length === 0 ? (
					<Typography sx={{ textAlign: "center", padding: 2 }}>
						Você não possui unidades distribuidoras cadastradas.
						<br />
						Redirecionando para o cadastro...
					</Typography>
				) : (
					<>
						<Typography sx={{ marginBottom: 2, color: "#666", fontSize: "14px" }}>
							Selecione a unidade distribuidora que deseja acessar:
						</Typography>
						<List sx={{ maxHeight: "400px", overflow: "auto" }}>
							{unidades.map((unidade) => (
								<ListItemButton
									key={unidade.id}
									onClick={() => handleSelectUnidade(unidade)}
									selected={selectedUnidade?.id === unidade.id}
									sx={{
										borderRadius: "8px",
										marginBottom: 1,
										border: "1px solid #E0E0E0",
										"&.Mui-selected": {
											backgroundColor: "#E0F7FA",
											borderColor: "#1a9e8f",
											"&:hover": {
												backgroundColor: "#B2EBF2",
											},
										},
										"&:hover": {
											backgroundColor: "#F5F5F5",
										},
									}}
								>
									<ListItemText
										primary={
											<Typography sx={{ fontWeight: 600, color: "#333" }}>
												{unidade.nomeFantasia || unidade.nome}
											</Typography>
										}
										secondary={
											<>
												<Typography component="span" sx={{ display: "block", fontSize: "14px", color: "#666" }}>
													{unidade.cpf_cnpj ? `CPF/CNPJ: ${unidade.cpf_cnpj}` : ""}
												</Typography>
												{unidade.endereco && (unidade.endereco.cidade || unidade.endereco.uf) && (
													<Typography component="span" sx={{ display: "block", fontSize: "12px", color: "#999" }}>
														{unidade.endereco.cidade && unidade.endereco.uf
															? `${unidade.endereco.cidade} - ${unidade.endereco.uf}`
															: unidade.endereco.cidade || unidade.endereco.uf || ""}
													</Typography>
												)}
											</>
										}
									/>
								</ListItemButton>
							))}
						</List>
					</>
				)}
			</DialogContent>

			{!loading && !error && unidades.length > 0 && (
				<DialogActions sx={{ justifyContent: "center", padding: "16px 24px" }}>
					<Button
						onClick={handleConfirm}
						disabled={!selectedUnidade}
						variant="contained"
						sx={{
							backgroundColor: "#1a9e8f",
							color: "white",
							padding: "12px 48px",
							fontSize: "16px",
							fontWeight: 600,
							borderRadius: "8px",
							"&:hover": {
								backgroundColor: "#138a7d",
							},
							"&:disabled": {
								backgroundColor: "#B0D4DB",
							},
						}}
					>
						Confirmar
					</Button>
				</DialogActions>
			)}
		</Dialog>
	);
}

export default UnidadeSelectorModal;
